import React, { useState, useEffect } from 'react';
import { doc, getDoc, runTransaction, addDoc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';

export const ShadowPollView = ({ confession }) => {
    const [options, setOptions] = useState(confession.pollOptions || []);
    const [totalVotes, setTotalVotes] = useState(confession.totalVotes || 0);
    const [userVote, setUserVote] = useState(null);
    const [loading, setLoading] = useState(true);
    const currentUser = auth?.currentUser;

    useEffect(() => {
        if (!currentUser) return;
        const fetchUserVote = async () => {
            try {
                const voteRef = doc(db, 'users', currentUser.uid, 'votes', confession.id);
                const voteSnap = await getDoc(voteRef);
                if (voteSnap.exists()) {
                    const data = voteSnap.data();
                    if (data.isActive !== false && data.optionId !== undefined) {
                        setUserVote(data.optionId);
                    }
                }
            } catch (err) {
                console.error("Error fetching vote", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUserVote();
    }, [currentUser, confession.id]);

    const handleVote = async (e, optionId) => {
        e.stopPropagation();
        if (!currentUser) return;

        const previousVote = userVote;
        const isRemoving = previousVote === optionId;
        const newVote = isRemoving ? null : optionId;

        // Optimistic UI
        setUserVote(newVote);
        setOptions(prev => prev.map(opt => {
            if (opt.id === previousVote) return { ...opt, voteCount: Math.max(0, (opt.voteCount || 0) - 1) };
            if (opt.id === newVote) return { ...opt, voteCount: (opt.voteCount || 0) + 1 };
            return opt;
        }));
        setTotalVotes(prev => prev + (isRemoving ? -1 : (previousVote !== null ? 0 : 1)));

        // Backend Update
        try {
            await runTransaction(db, async (transaction) => {
                const confRef = doc(db, 'confessions', confession.id);
                const voteRef = doc(db, 'users', currentUser.uid, 'votes', confession.id);

                const cDoc = await transaction.get(confRef);
                const vDoc = await transaction.get(voteRef);

                if (!cDoc.exists()) throw new Error("Confession not found");

                const currentConf = cDoc.data();
                let newOptions = [...(currentConf.pollOptions || [])];
                let newTotal = currentConf.totalVotes || 0;

                let shouldNotify = false;
                let isFirstVote = !vDoc.exists();

                if (isFirstVote) {
                    shouldNotify = true;
                } else {
                    const data = vDoc.data();
                    const lastVotedAt = data.votedAt?.toDate() || new Date(0);
                    const now = new Date();
                    const hoursSinceLastVote = (now - lastVotedAt) / (1000 * 60 * 60);
                    if (hoursSinceLastVote > 24) {
                        shouldNotify = true;
                    }
                }

                if (vDoc.exists()) {
                    const data = vDoc.data();
                    const currentlyActive = data.isActive !== false;
                    const oldOptionId = data.optionId;

                    if (currentlyActive && oldOptionId !== undefined && oldOptionId !== null) {
                        if (oldOptionId === optionId) {
                            // Remove vote
                            newOptions = newOptions.map(opt => 
                                opt.id === optionId ? { ...opt, voteCount: Math.max(0, (opt.voteCount || 0) - 1) } : opt
                            );
                            newTotal = Math.max(0, newTotal - 1);
                            transaction.update(voteRef, { isActive: false, optionId: null });
                            shouldNotify = false;
                        } else {
                            // Switch vote
                            newOptions = newOptions.map(opt => {
                                if (opt.id === oldOptionId) return { ...opt, voteCount: Math.max(0, (opt.voteCount || 0) - 1) };
                                if (opt.id === optionId) return { ...opt, voteCount: (opt.voteCount || 0) + 1 };
                                return opt;
                            });
                            transaction.update(voteRef, { optionId, isActive: true, votedAt: new Date() });
                        }
                    } else {
                        // Resurrect vote
                        newOptions = newOptions.map(opt => 
                            opt.id === optionId ? { ...opt, voteCount: (opt.voteCount || 0) + 1 } : opt
                        );
                        newTotal += 1;
                        transaction.update(voteRef, { optionId, isActive: true, votedAt: new Date() });
                    }
                } else {
                    // New vote
                    newOptions = newOptions.map(opt => 
                        opt.id === optionId ? { ...opt, voteCount: (opt.voteCount || 0) + 1 } : opt
                    );
                    newTotal += 1;
                    transaction.set(voteRef, { optionId, isActive: true, votedAt: new Date() });
                }

                transaction.update(confRef, { pollOptions: newOptions, totalVotes: newTotal });

                if (shouldNotify && confession.userId !== currentUser.uid) {
                    const votedOption = newOptions.find(o => o.id === optionId);
                    const optionText = votedOption ? votedOption.text : 'poll';
                    const notifRef = doc(collection(db, 'users', confession.userId, 'notifications'));
                    transaction.set(notifRef, {
                        type: 'poll_vote',
                        postId: confession.id,
                        confessionId: confession.id,
                        isConfession: true,
                        fromUserId: currentUser.uid,
                        optionText: optionText,
                        createdAt: serverTimestamp(),
                        read: false
                    });
                }
            });
        } catch (err) {
            console.error("Poll transaction failed:", err);
            // Revert on failure (simple reload or ignore for now)
        }
    };

    if (loading) return <div className="animate-pulse h-20 bg-gray-900 rounded-xl mt-3"></div>;

    return (
        <div className="mt-3 flex flex-col gap-2">
            {options.map(opt => {
                const isSelected = userVote === opt.id;
                const percentage = totalVotes > 0 ? Math.round((opt.voteCount / totalVotes) * 100) : 0;
                
                return (
                    <div 
                        key={opt.id}
                        onClick={(e) => handleVote(e, opt.id)}
                        className={`relative overflow-hidden rounded-xl border ${isSelected ? 'border-[#0BD9E5]' : 'border-gray-800'} bg-gray-900/50 cursor-pointer group transition-all`}
                    >
                        {/* Progress Bar Background */}
                        <div 
                            className={`absolute top-0 left-0 h-full transition-all duration-500 ease-out ${isSelected ? 'bg-[#0BD9E5]/20' : 'bg-gray-800/50'}`}
                            style={{ width: `${userVote !== null ? percentage : 0}%` }}
                        />
                        
                        <div className="relative z-10 flex items-center justify-between p-3 px-4">
                            <span className={`text-[15px] font-medium z-10 ${isSelected ? 'text-[#0BD9E5]' : 'text-gray-200'}`}>
                                {opt.text}
                            </span>
                            {userVote !== null && (
                                <span className={`text-sm font-bold ${isSelected ? 'text-[#0BD9E5]' : 'text-gray-400'}`}>
                                    {percentage}%
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
            <div className="text-gray-500 text-xs text-right mt-1 font-medium">{totalVotes} votes</div>
        </div>
    );
};
